import { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { MediaAttachment } from "./mediaAttachmentExtension";
import { subirAdjunto } from "../../services/ticketsService";

interface TicketEditorProps {
  content?: unknown;
  onChange?: (json: unknown) => void;
  placeholder?: string;
  editable?: boolean;
}

function attachmentKind(file: File): "image" | "video" | "file" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

// El plugin @tailwindcss/typography no está instalado en este proyecto, así
// que las clases `prose` no existen: damos el mismo look con selectores de
// hijos en vez de depender de esa plugin.
const RICH_CONTENT_CLASS =
  "text-sm leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 " +
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 " +
  "[&_strong]:font-semibold [&_em]:italic [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mb-2 " +
  "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mb-2 [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded";

export function TicketEditor({ content, onChange, placeholder, editable = true }: TicketEditorProps) {
  const editorRef = useRef<Editor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = useCallback((files: File[]): boolean => {
    if (files.length === 0) return false;

    files.forEach(async (file) => {
      const toastId = toast.loading(`Subiendo ${file.name}...`);
      try {
        const { path } = await subirAdjunto(file);
        const kind = attachmentKind(file);
        editorRef.current
          ?.chain()
          .focus()
          .insertMediaAttachment({ path, kind, name: file.name })
          .run();
        toast.success(`${file.name} subido`, { id: toastId });
      } catch (err: any) {
        toast.error(`No se pudo subir ${file.name}: ${err.message || ""}`, { id: toastId });
      }
    });

    return true;
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder:
          placeholder ??
          "Describe el problema... puedes pegar capturas de pantalla, video o adjuntar archivos.",
      }),
      MediaAttachment,
    ],
    content: (content as any) ?? "",
    editable,
    editorProps: {
      attributes: {
        class: `${RICH_CONTENT_CLASS} min-h-[160px] focus:outline-none`,
      },
      handlePaste: (_view, event) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        const files = items
          .map((item) => (item.kind === "file" ? item.getAsFile() : null))
          .filter((f): f is File => !!f);
        return handleFiles(files);
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        const handled = handleFiles(files);
        if (handled) event.preventDefault();
        return handled;
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getJSON()),
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Solo re-hidratamos contenido externo en modo lectura (comentarios/ticket
  // cargados async). En modo edición el contenido nace vacío y no queremos
  // pelearnos con el cursor del usuario.
  useEffect(() => {
    if (editor && !editable && content !== undefined) {
      editor.commands.setContent(content as any);
    }
  }, [editor, content, editable]);

  if (!editable) {
    return (
      <div className={RICH_CONTENT_CLASS}>
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div className="border rounded-md bg-background">
      <div className="flex items-center gap-1 border-b px-2 py-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={editor?.isActive("bold") ? "bg-accent" : ""}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={editor?.isActive("italic") ? "bg-accent" : ""}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={editor?.isActive("bulletList") ? "bg-accent" : ""}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={editor?.isActive("orderedList") ? "bg-accent" : ""}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
          <Paperclip className="h-4 w-4" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(Array.from(e.target.files));
            e.target.value = "";
          }}
        />
      </div>
      <div className="px-3 py-2">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
